<script lang="ts" setup>
import { ref, PropType, computed, watch, nextTick } from "vue";
import {
  IconButton,
  Tooltip,
  Dropdown,
  UserAvatar,
  UpgradeModal,
} from "@/components";
import {
  type User,
  useDocumentSpaceConfigsQuery,
  useUpdateDocumentSpaceConfigMutation,
  DocumentSpace,
} from "@/graphql/operations";
import { useStore } from "vuex";
import DisableTrackingModal from "./DisableTrackingModal.vue";
import DeleteSpaceModal from "../DeleteSpaceModal.vue";
import AccessModal from "./access_modal/AccessModal.vue";
import CRMContactInfo from "./CRMContactInfo.vue";
import ShareMenu from "./share_menu/ShareMenu.vue";
import CopySpaceModal from "../CopySpaceModal.vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useSpaceModalDisplay } from "@/composables";

const {
  displayCopyDocumentSpaceModal,
  displayUpgradeModal,
  copyDocumentSpaceModalVisible,
  upgradeModalVisible,
  handleModalDisplay,
} = useSpaceModalDisplay();

const { t } = useI18n({});
const store = useStore();
const router = useRouter();
const user = store.getters.user;

const props = defineProps({
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
  emailVerificationValid: {
    type: Boolean,
    required: true,
  },
});

const name = ref<string>(props.documentSpace.name || "");
const currentName = ref<string>(props.documentSpace.name || "");

watch(props, () => {
  if (!props.documentSpace.name) {
    return;
  }

  if (props.documentSpace.name !== name.value) {
    if (currentName.value === name.value) {
      currentName.value = props.documentSpace.name;
    }
    name.value = props.documentSpace.name;
  }
});

const emit = defineEmits([
  "success",
  "refreshData",
  "update:emailVerificationValid",
]);

const { data: spaceConfigData } = useDocumentSpaceConfigsQuery({});
const spaceConfigs = computed(() => {
  return spaceConfigData.value?.documentSpaceConfigs?.nodes.map(
    (config: any) => ({ value: config?.id, title: config?.name }),
  );
});

const configError = ref(false);
const selectedConfig = ref(props.documentSpace.config?.id?.toString());
watch(
  () => props.documentSpace.config?.id,
  (id) => {
    selectedConfig.value = id?.toString();
  },
);
const { executeMutation: executeUpdateConfig, fetching: savingConfig } =
  useUpdateDocumentSpaceConfigMutation();
const updateConfig = async (configId: string) => {
  if (savingConfig.value) return;
  configError.value = false;
  selectedConfig.value = configId;
  const result = await executeUpdateConfig({
    id: props.documentSpace.id,
    configId: configId,
  });

  if (result.error || !result.data?.updateDocumentSpace) {
    selectedConfig.value = props.documentSpace.config?.id?.toString();
    configError.value = true;
    return;
  }
  emit("success");
};

const crmContact = computed(
  () => props.documentSpace.documentSpaceCrmMapping?.crmContact,
);
const hasCRMMapping = computed(
  () => crmContact.value !== null && crmContact.value !== undefined,
);

// Colleagues who already created an (invited or visited) space for this contact.
// The backend gates this to authors I'm allowed to see; we just drop myself so
// only the "from whom" colleagues remain. Tenants worried about internal
// competition can hide this list entirely via the `disable_other_space_authors`
// flag — purely client-side, the data itself stays RLS-gated and queryable.
const spaceAuthors = computed(() => {
  if (user.hidesOtherSpaceAuthors()) return [];
  return (crmContact.value?.authorsOfDocumentSpaces?.nodes ?? []).filter(
    (author) =>
      // An author is given
      author &&
      // and it's not me
      author.id !== user.postgresId &&
      // and it has a display name
      author.displayName,
  ) as Array<User & { id: string; displayName: string }>;
});

const redirectDocumentSpaces = () => {
  router.push({ name: "DocumentSpaces" });
};
const deleteConfirmationActive = ref<boolean>(false);
const showDeleteConfirmation = () => (deleteConfirmationActive.value = true);

const canDisableSpaceTracking = computed(() =>
  store.getters.user.canDisableSpaceTracking(),
);

const displayDisableTrackingModal = ref<boolean>(false);

const myAccess = computed(() =>
  props.documentSpace.documentSpaceAccesses.nodes.find(
    (a) => a?.user?.id === store.getters.user.postgresId,
  ),
);

const actionItems = computed(() => {
  const i = [];

  i.push({
    value: "copy",
    title: t("documentSpace.copy.buttonText"),
  });

  if (canDisableSpaceTracking.value) {
    i.push({
      value: "disableTracking",
      title: t("documentSpace.disableTracking.buttonText"),
    });
  }

  if (myAccess.value?.role === "owner") {
    i.push({
      value: "delete",
      title: t("documentSpace.delete.buttonText"),
      destructive: true,
    });
  }

  return i;
});

const handleAction = async (action: string) => {
  switch (action) {
    case "delete":
      showDeleteConfirmation();
      break;

    case "disableTracking":
      displayDisableTrackingModal.value = true;
      break;

    case "copy":
      await handleModalDisplay("copy");
      break;
  }
};

const accessModalActive = ref<boolean>(false);
const accessModalOpener = ref<HTMLElement>();
const displayAccessModal = (event: Event) => {
  accessModalOpener.value = event.currentTarget as HTMLElement;
  accessModalActive.value = true;
};
const closeAccessModal = () => {
  accessModalActive.value = false;
  void nextTick(() => accessModalOpener.value?.focus());
};

const crmContactInfoActive = ref<boolean>(false);
const crmContactInfoOpener = ref<HTMLElement>();
const displayCRMContactInfo = (event: Event) => {
  crmContactInfoOpener.value = event.currentTarget as HTMLElement;
  crmContactInfoActive.value = true;
};
const closeCRMContactInfo = () => {
  crmContactInfoActive.value = false;
  void nextTick(() => crmContactInfoOpener.value?.focus());
};
</script>

<template>
  <div class="controls">
    <div class="row">
      <template v-if="user.hasSpaceCollab()">
        <div class="access_box">
          <Tooltip
            class="bottom"
            v-for="access in documentSpace.documentSpaceAccesses.nodes"
            :key="access!.id"
          >
            <template v-slot>
              <button
                type="button"
                class="avatar_button"
                data-test="open-shared-space-access"
                :aria-label="`${$t('documentSpace.access.title')}: ${access!.user!.displayName}`"
                @click="displayAccessModal"
              >
                <user-avatar
                  :name="access!.user!.displayName!"
                  :email="access!.user!.email!"
                  :profileImgUrl="
                    access!.user!.userContactDetail?.profileImageUrl ||
                    undefined
                  "
                  :appearance="'subtle'"
                />
              </button>
            </template>
            <template #tooltip>
              {{ access!.user!.displayName }}
            </template>
          </Tooltip>
        </div>
      </template>

      <div class="right">
        <div v-if="documentSpace.trackingDeactivated" class="control">
          <IconButton :icon="'location_disabled'" :disabled="true">
            {{ $t("documentSpace.disableTracking.textDeactivated") }}
          </IconButton>
        </div>

        <div class="control">
          <Dropdown
            :items="actionItems"
            @change="handleAction"
            data-test="shared-space-actions"
          >
            <div>
              <span class="material-symbols-outlined">more_vert</span>
            </div>
          </Dropdown>
        </div>

        <share-menu
          :documentSpace="documentSpace"
          :emailVerificationValid="emailVerificationValid"
          @refreshData="emit('refreshData')"
          @update:emailVerificationValid="
            emit('update:emailVerificationValid', $event)
          "
        />
      </div>
    </div>
  </div>
  <div
    class="controls"
    v-if="(spaceConfigs && spaceConfigs.length > 1) || hasCRMMapping"
  >
    <div class="control" v-if="spaceConfigs && spaceConfigs.length > 1">
      <label>Template:</label>
      <Dropdown
        :items="spaceConfigs"
        :value="selectedConfig"
        :inert="savingConfig"
        :aria-busy="savingConfig"
        @change="updateConfig"
        :displayTitle="true"
      >
      </Dropdown>
    </div>

    <p v-if="configError" role="alert" class="save_error">{{ $t("error") }}</p>

    <div class="control" v-if="crmContact">
      <label>Assigned To:</label>
      <button
        type="button"
        class="crm_contact_button"
        data-test="open-crm-contact"
        @click="displayCRMContactInfo"
      >
        {{ crmContact!.firstName }} {{ crmContact!.lastName }}
      </button>
    </div>

    <div class="control other_spaces_by" v-if="spaceAuthors.length">
      <label>Other Spaces By:</label>
      <span class="authors">
        <span v-for="author in spaceAuthors" :key="author.id">{{
          author.displayName
        }}</span>
      </span>
    </div>
  </div>

  <disable-tracking-modal
    :active="displayDisableTrackingModal"
    :documentSpaceId="documentSpace.id"
    @success="emit('success')"
    @close="displayDisableTrackingModal = false"
  />

  <delete-space-modal
    :active="deleteConfirmationActive"
    :space-id="documentSpace.id"
    :name="documentSpace.name || ''"
    @close="deleteConfirmationActive = false"
    @success="redirectDocumentSpaces"
  />

  <access-modal
    v-if="accessModalActive"
    :documentSpace="documentSpace"
    @close="closeAccessModal"
    @refreshData="emit('refreshData')"
  />

  <CRMContactInfo
    v-if="crmContactInfoActive && crmContact"
    :crmContact="crmContact!"
    @close="closeCRMContactInfo"
  />

  <upgrade-modal
    v-if="upgradeModalVisible"
    :inherit-classic-theme="true"
    @close="displayUpgradeModal = false"
  />

  <CopySpaceModal
    v-if="copyDocumentSpaceModalVisible"
    :inherit-classic-theme="true"
    @close="displayCopyDocumentSpaceModal = false"
    :sourceSpaceId="documentSpace.id"
    :sourceSpaceName="documentSpace.name || ''"
  />
</template>

<style scoped lang="scss">
.save_error {
  padding: 0.75em;
  color: var(--classic-error-text, #9f3a38);
  background: var(--classic-error-surface, #fff6f6);
}

.controls {
  margin: 0 1em;
  padding: 1em 0;
  border-bottom: 1px solid var(--classic-border, #dbdbdb);
}

.button.right {
  float: right;
  margin-left: 5px;
}

.row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 0.5em;
}

.row .right {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  max-width: 100%;
}

.button .material-symbols-outlined {
  font-size: 1em;
  line-height: 1.5;
  vertical-align: middle;
  margin-right: 5px;
}

textarea {
  max-width: 100%;
  min-height: 10em;
  margin-bottom: 5px;
}

.info {
  text-align: center;
  font-style: italic;
}

.control label {
  display: inline-block;
  padding: 0.35rem 0;
  margin-right: 3px;
  line-height: 1.5;
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  width: 114px;
}

.control :deep(.dropdown_title) {
  padding-left: 0;
}

// Stack the authors in the value column (table look), first line on the label's
// baseline. Matching the label's vertical padding + line-height keeps the first
// name aligned with the "Other Spaces By:" label.
.other_spaces_by {
  .authors {
    display: inline-block;
    vertical-align: top;
    padding: 0.35rem 0;
    line-height: 1.5;

    span {
      display: block;
    }
  }
}

.circle_hover {
  border-radius: 50%;
  padding: 0.25em;
}

.circle_hover:hover {
  background: var(--classic-surface-hover, #eee);
}

.access_box {
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  margin-right: 10px;
}

.avatar_button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.crm_contact_button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.access_box .avatar {
  margin-right: -10px;
  cursor: pointer;
}

.dropdown :deep(.dropdown_trigger .dropdown_title) {
  border: none;
}
</style>
